import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import type { FieldPolicy } from '@apollo/client/cache'
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { PersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { RetryLink } from '@apollo/client/link/retry'
import {
  getMainDefinition,
  Observable,
  relayStylePagination,
} from '@apollo/client/utilities'
import { captureException } from '@sentry/browser'
import { Kind, OperationTypeNode } from 'graphql'
import Cookies from 'js-cookie'
import { withBase, withQuery } from 'ufo'
import { bypassAdmin } from 'src/stores/admin'
import { getApiUrl } from 'src/utils/config'
import { getFingerprint } from 'src/utils/fingerprint'
import { sha256 } from 'src/utils/hash'
import {
  setIntercomUserJwt,
  unsetIntercomUserJwt,
} from 'src/utils/intercom/storage'
import { getToken, setToken } from 'src/utils/token'
import { setIPCountry } from '../helpers/ip'
import type { StrictTypedTypePolicies } from './generated/graphql'
import { createApolloWsLink } from './wsLink'

type KeyArgs = FieldPolicy['keyArgs']

export interface ApolloClientInitialContext {
  refetchOnFocus?: boolean
  refetchOnReconnect?: boolean
  fetchOptions?: RequestInit
  headers?: Record<string, string>
}

export interface ApolloClientContext extends ApolloClientInitialContext {
  uri?: string
  response?: Response
  hasAuth?: boolean
}

const baseURL = getApiUrl()

const httpLink = new HttpLink({
  uri: baseURL,
  credentials: 'include',
})

const persistedQueryLink = new PersistedQueryLink({
  sha256,
  useGETForHashedQueries: true,
})

const retryLink = new RetryLink({
  delay: {
    initial: 5000,
    max: 5000,
  },
  attempts: {
    max: 10,
    // see https://www.apollographql.com/docs/react/api/link/apollo-link-http#handling-errors for error types
    retryIf(error, operation) {
      const operationDefinition = getMainDefinition(operation.query)
      const isQuery =
        operationDefinition.kind === Kind.OPERATION_DEFINITION &&
        operationDefinition.operation === OperationTypeNode.QUERY
      if (!isQuery) {
        return false
      }
      if ('parseError' in error) {
        if (error.parseError) {
          return false
        }
      }
      if (ServerError.is(error)) {
        if (error.statusCode >= 400 && error.statusCode < 500) {
          return false
        }
      }
      return !!error
    },
  },
})

/**
 * parse udt from cookie if present
 */
const getUdtFromCookie = () => Cookies.get('_udt')

// SetContextLink: v4 swaps args - (prevContext, operation) instead of (operation, prevContext)
const setContextLink = new SetContextLink((prevContext, operation) => {
  const ctx = prevContext as ApolloClientContext
  const token = getToken()
  const headers: Record<string, string> = { ...ctx.headers }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  if (bypassAdmin()) {
    headers['X-Bypass-Admin'] = '1'
  }

  const browserId = getFingerprint()
  if (browserId) {
    headers['X-Browser-ID'] = browserId
  }

  const queryParams: Record<string, string | undefined> = {
    operation: operation.operationName,
  }

  // a little bit of name obfuscation
  const u3t = getUdtFromCookie()
  if (u3t) {
    queryParams.u3t = u3t
  }

  return {
    ...ctx,
    headers,
    hasAuth: !!token,
    uri: withQuery(withBase('/graphql', baseURL), queryParams),
  }
})

// ErrorLink for handling 401 logout
const logoutLink = new ErrorLink(({ error, operation }) => {
  if (!ServerError.is(error) || error.statusCode !== 401) return
  // Only wipe when the failing request actually carried a token.
  // Otherwise an unauthenticated request that lands after a sibling
  // login flow has just stored a fresh token would clobber it.
  const ctx = operation.getContext() as ApolloClientContext
  if (!ctx.hasAuth) return
  setToken('')
  unsetIntercomUserJwt()
})

const handleHeaderLink = new ApolloLink((operation, forward) => {
  return new Observable(observer => {
    const subscription = forward(operation).subscribe({
      next(value) {
        observer.next(value)
      },
      error(errorValue) {
        observer.error(errorValue)
      },
      complete() {
        const res = (operation.getContext() as ApolloClientContext).response
        const token = res?.headers.get('token')
        if (token) {
          setToken(token)
        }
        const intercomUserJwt = res?.headers.get('x-intercom-user-jwt')
        if (intercomUserJwt) {
          setIntercomUserJwt(intercomUserJwt)
        }
        const ipCountry = res?.headers.get('x-country-code')
        if (ipCountry) {
          setIPCountry(ipCountry)
        }
        observer.complete()
      },
    })
    return () => subscription.unsubscribe()
  })
})

// Drop introspection query from Apollo Client DevTools
const dropIntrospectionLink = new ApolloLink((operation, forward) => {
  if (operation.operationName === 'IntrospectionQuery') {
    // eslint-disable-next-line no-console
    console.debug('Dropped IntrospectionQuery from Apollo Client Devtools')
    return new Observable(observer => {
      observer.complete()
    })
  }
  return forward(operation)
})

// ErrorLink for reporting GraphQL errors to Sentry
const reportErrorsToSentryLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    // Only BAD_USER_INPUT errors should be reported
    error.errors
      .filter(it => it.extensions?.code === 'BAD_USER_INPUT')
      .forEach(graphqlError => {
        const err = new Error(graphqlError.message)
        err.name = 'GRAPHQL_BAD_USER_INPUT'
        captureException(err)
      })
  }
})

/**
 * Apollo client use query arguments as cache key
 * This function automatically generate the cache key for infinity query
 * by preserve all non-pagination arguments
 * See https://www.apollographql.com/docs/react/pagination/key-args
 */
const relayStylePaginationKeyArgs: KeyArgs = args => {
  if (args == null) {
    return
  }
  return Object.keys(args).filter(
    key =>
      key !== 'before' && key !== 'after' && key !== 'first' && key !== 'last',
  )
}

/**
 * Similar to relayStylePaginationKeyArgs
 * This function preserves all arguments except cursor related ones
 * Prevents caching issue when different pageSize queries exist
 */
const relayStyleWithoutCursorKeyArgs: KeyArgs = args => {
  if (args == null) {
    return
  }
  return Object.keys(args).filter(key => key !== 'before' && key !== 'after')
}

const typePolicies: StrictTypedTypePolicies = {
  Query: {
    fields: {
      user: {
        merge(existing, incoming, { mergeObjects }) {
          if (!existing) return incoming
          if (!incoming) return existing
          return mergeObjects(existing, incoming)
        },
      },
      messages: relayStylePagination(relayStylePaginationKeyArgs),
      artworks: relayStylePagination(relayStylePaginationKeyArgs),
      tasks: relayStylePagination(relayStylePaginationKeyArgs),
      taskSummaries: relayStylePagination(relayStylePaginationKeyArgs),
      workflows: relayStylePagination(relayStylePaginationKeyArgs),
      contests: relayStylePagination(relayStylePaginationKeyArgs),
      generationModels: relayStylePagination(relayStyleWithoutCursorKeyArgs),
    },
  },
  Workflow: {
    fields: {
      versions: relayStylePagination(relayStylePaginationKeyArgs),
    },
  },
  User: {
    fields: {
      tasks: relayStylePagination(relayStylePaginationKeyArgs),
      taskSummaries: relayStylePagination(relayStylePaginationKeyArgs),
      bookmarkedGenerationModels: relayStylePagination(
        relayStyleWithoutCursorKeyArgs,
      ),
    },
  },
  GenerationModel: {
    fields: {
      flag: {
        // ModerationFlagPreview and ModerationFlagDetail are safe to merge
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming)
        },
      },
    },
  },
}

export function createApolloCache() {
  return new InMemoryCache({
    typePolicies,
  })
}

export const cache = createApolloCache()

// Default config for additional features (like refetching) goes here
export const defaultContext: Required<ApolloClientInitialContext> = {
  refetchOnFocus: true,
  refetchOnReconnect: true,
  fetchOptions: {},
  headers: {},
}

// Create WebSocket link
const wsLink = createApolloWsLink()

// Split link: route subscriptions to WS, queries/mutations to HTTP
const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  ApolloLink.from([setContextLink, retryLink, persistedQueryLink, httpLink]),
)

export const baseApolloClient = new ApolloClient({
  link: ApolloLink.from([
    dropIntrospectionLink,
    reportErrorsToSentryLink,
    handleHeaderLink,
    logoutLink,
    splitLink,
  ]),
  cache,
  defaultContext,
  /**
   * query is for client.query, watchQuery is for useQuery (which internally use client.watchQuery)
   * @see https://www.apollographql.com/docs/react/api/core/ApolloClient#defaultoptions
   */
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      // v4: Restore v3 behavior - don't set loading=true during refetch
      notifyOnNetworkStatusChange: false,
    },
  },
})
